import ProductCard from '@v2/shared/components/ProductCard';
import { Product } from '@v2/shared/lib/types';

type Props = {
  products: Product[];
  /** The GA4 `item_list_name` every card reports under. */
  list: string;
  /** Where this page starts in the whole result set, so positions stay absolute. */
  startOffset: number;
  /** Passed to each card; see ProductCard. */
  showMsrp: boolean;
};

/**
 * How wide a card is drawn, measured on the page. The container has a fixed width at each
 * breakpoint, so the columns do too: two up to md, 175px wide from sm and 156px below it
 * (narrower on a phone under 375px); then three — beside the filter rail from lg — four
 * from xl and five from 2xl, all of them 217–227px wide.
 */
const CARD_WIDTHS = [
  { media: '(min-width: 768px)', width: 227 },
  { media: '(min-width: 414px)', width: 175 },
  { width: 156 },
];

/**
 * The listing grid.
 *
 * `ProductCard` — the same component the home page's bands draw, which is what "product
 * cards should match the other cards" asks for and also what stops the listing drifting
 * away from the rest of the site the next time a card changes. The one thing it leaves
 * off is the "Save X%" line, which a listing of sixteen cards does not want repeated under
 * every price.
 *
 * Every cover on the page gets the standard 3:4 box, so the names and prices below them
 * line up instead of going ragged. Not `coverAspectFor`: the covers come with their own
 * sizes, and it would give a page of books the tall box, where the grid has always been 3:4.
 */
export default function ProductGrid({ products, list, startOffset, showMsrp }: Props) {
  if (products.length === 0) {
    return (
      <p className="rounded-xl border border-solid border-slate-200 bg-white px-5 py-8 text-center text-slate-600">
        No magazines match these filters.
      </p>
    );
  }

  return (
    <ul className="m-0 grid list-none grid-cols-2 gap-6 p-0 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {products.map((product, index) => (
        <li key={product.id}>
          <ProductCard
            product={product}
            widths={CARD_WIDTHS}
            coverAspect="standard"
            showSavings={false}
            showMsrp={showMsrp}
            list={list}
            index={startOffset + index + 1}
          />
        </li>
      ))}
    </ul>
  );
}
